import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import PhoneInput from "@/components/PhoneInput";
import {
  Luggage,
  MapPin,
  Clock,
  ShieldCheck,
  CalendarIcon,
  Mail,
  MessageCircle,
  Euro,
  ChevronRight,
  Menu,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default images
const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1663667657066-1f2fa8265473?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxCYXJpJTIwSXRhbHklMjBvbGQlMjB0b3duJTIwc3RyZWV0cyUyMHN1bm55JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc2ODU5ODYxN3ww&ixlib=rb-4.1.0&q=85";
const DEFAULT_FEATURE_IMAGE1 = "https://images.unsplash.com/photo-1700223797363-eac22c5a10ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxsdWdnYWdlJTIwc3VpdGNhc2VzJTIwc3RvcmFnZSUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzY4NTk4NjIxfDA&ixlib=rb-4.1.0&q=85";
const DEFAULT_FEATURE_IMAGE2 = "https://images.unsplash.com/photo-1578897173331-5a2a737c11ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHRyYXZlbGVycyUyMHdhbGtpbmclMjBjaXR5JTIwY2FyZWZyZWV8ZW58MHx8fHwxNzY4NTk4NjIwfDA&ixlib=rb-4.1.0&q=85";

const generateTimeOptions = () => {
  const times = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

export default function HomePage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sameAsWhatsApp, setSameAsWhatsApp] = useState(true);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    whatsapp_number: "",
    number_of_bags: 1,
    pickup_date: null,
    pickup_time: "",
    dropoff_date: null,
    dropoff_time: "",
    pricing_type: "auto" // auto, hourly, daily
  });

  const [priceInfo, setPriceInfo] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-fill WhatsApp when mobile changes and checkbox is checked
  useEffect(() => {
    if (sameAsWhatsApp && formData.mobile_number) {
      setFormData(prev => ({ ...prev, whatsapp_number: prev.mobile_number }));
    }
  }, [formData.mobile_number, sameAsWhatsApp]);

  useEffect(() => {
    if (formData.dropoff_date && formData.dropoff_time && formData.pickup_date && formData.pickup_time && formData.number_of_bags > 0) {
      calculatePrice();
    } else {
      setPriceInfo(null);
    }
  }, [formData.pickup_date, formData.pickup_time, formData.dropoff_date, formData.dropoff_time, formData.number_of_bags, formData.pricing_type]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
      console.log(response.data);
      if (response.data.logo_url) {
        setLogoUrl(`${API}/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  const calculatePrice = async () => {
    setIsCalculating(true);
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        pickup_date: format(formData.pickup_date, "yyyy-MM-dd"),
        pickup_time: formData.pickup_time,
        dropoff_date: format(formData.dropoff_date, "yyyy-MM-dd"),
        dropoff_time: formData.dropoff_time,
        number_of_bags: formData.number_of_bags,
        pricing_type: formData.pricing_type
      });
      setPriceInfo(response.data);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
      setPriceInfo(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.pickup_date || !formData.dropoff_date || !formData.pickup_time || !formData.dropoff_time) {
      toast.error("Please select pickup and drop-off dates and times");
      return;
    }

    if (!priceInfo) {
      toast.error("Please wait for price calculation");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        pickup_date: format(formData.pickup_date, "yyyy-MM-dd"),
        dropoff_date: format(formData.dropoff_date, "yyyy-MM-dd"),
        pricing_type: formData.pricing_type
      });

      toast.success("Booking created successfully!");
      navigate(`/booking/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBooking = () => {
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get dynamic content with fallback
  const getContent = (obj, lang) => {
    if (!obj) return "";
    return obj[lang] || obj.en || "";
  };

  // Dynamic content from config
  const heroTagline = config?.hero ? getContent(config.hero.tagline, language) : t('hero.tagline');
  const heroTitle1 = config?.hero ? getContent(config.hero.title1, language) : t('hero.title1');
  const heroTitle2 = config?.hero ? getContent(config.hero.title2, language) : t('hero.title2');
  const heroDescription = config?.hero ? getContent(config.hero.description, language) : t('hero.description');
  const heroImage = config?.hero?.image || DEFAULT_HERO_IMAGE;

  const features = config?.features || [];
  const featureImage1 = config?.feature_images?.image1 || DEFAULT_FEATURE_IMAGE1;
  const featureImage2 = config?.feature_images?.image2 || DEFAULT_FEATURE_IMAGE2;

  const footerDesc = config?.footer ? getContent(config.footer.description, language) : t('footer.description');

  // SEO Meta content
  const businessName = config?.business_name || "Bari Bird Luggage Storage";
  const seoTitle = `${businessName} | Secure Luggage Storage in Bari, Italy`;
  const seoDescription = config?.seo_description || `Affordable and secure luggage storage in Bari, Italy. Store your bags from €${config?.price_per_bag_per_hour || 1.5}/hour or €${config?.price_per_bag_per_day || 5}/day. Central location near train station.`;
  const seoKeywords = config?.seo_keywords || "luggage storage Bari, bag storage Italy, left luggage Bari, deposito bagagli Bari, luggage locker Puglia";

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:locale" content={language === 'it' ? 'it_IT' : language === 'es' ? 'es_ES' : language === 'ro' ? 'ro_RO' : 'en_US'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={heroImage} />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="geo.region" content="IT-BA" />
        <meta name="geo.placename" content="Bari" />
        <meta name="geo.position" content="41.1171;16.8719" />
        <link rel="canonical" href={window.location.origin} />

        {/* Schema.org LocalBusiness structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": businessName,
            "description": seoDescription,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": config?.business_address || "Via Andrea da Bari, 122",
              "addressLocality": "Bari",
              "addressRegion": "Puglia",
              "postalCode": "70121",
              "addressCountry": "IT"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 41.1171,
              "longitude": 16.8719
            },
            "telephone": config?.business_phone || "+393808972255",
            "priceRange": "€€",
            "openingHours": "Mo-Su 06:00-22:00",
            "image": heroImage,
            "url": window.location.origin
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-[#004E64]/5 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <a href="/" className="flex items-center gap-2">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain" />
                ) : (
                  <Luggage className="w-8 h-8 text-[#004E64]" />
                )}
                <span className="font-serif text-xl font-semibold text-[#004E64] hidden sm:inline">
                  {config?.business_name}
                </span>
              </a>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="font-medium text-[#004E64] hover:text-[#FFB400] transition-colors">{t('nav.features')}</a>
              <a href="#location" className="font-medium text-[#004E64] hover:text-[#FFB400] transition-colors">{t('nav.location')}</a>
              {/* <a href="/admin/login" className="font-medium text-[#004E64] hover:text-[#FFB400] transition-colors">{t('nav.admin')}</a> */}
              <LanguageSelector />
              <Button onClick={scrollToBooking} className="bg-[#FFB400] hover:bg-[#E5A200] text-[#004E64] font-bold rounded-full px-6">
                {t('nav.bookNow')}
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector />
              <button className="p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X className="w-6 h-6 text-[#004E64]" /> : <Menu className="w-6 h-6 text-[#004E64]" />}
              </button>
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#004E64]/10">
              <div className="flex flex-col gap-4">
                <a href="#features" className="font-medium text-[#004E64]" onClick={() => setIsMenuOpen(false)}>{t('nav.features')}</a>
                <a href="#location" className="font-medium text-[#004E64]" onClick={() => setIsMenuOpen(false)}>{t('nav.location')}</a>
                {/* <a href="/admin/login" className="font-medium text-[#004E64]" onClick={() => setIsMenuOpen(false)}>{t('nav.admin')}</a> */}
                <Button onClick={() => { scrollToBooking(); setIsMenuOpen(false); }} className="bg-[#FFB400] hover:bg-[#E5A200] text-[#004E64] font-bold rounded-full">
                  {t('nav.bookNow')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[90vh]">
          <div className="lg:col-span-7 flex items-center px-6 md:px-12 lg:px-16 py-12 lg:py-0">
            <div className="max-w-xl animate-fadeIn">
              <p className="text-xs font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{heroTagline}</p>
              <h1 className="font-serif text-5xl md:text-7xl font-semibold tracking-tight text-[#004E64] leading-[1.1] mb-6">
                {heroTitle1}<br />
                <span className="text-[#FFB400]">{heroTitle2}</span>
              </h1>
              <p className="font-sans text-lg md:text-xl text-[#5A6B7C] leading-relaxed mb-8">{heroDescription}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToBooking}
                  className="bg-[#004E64] hover:bg-[#003848] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:-translate-y-1 transition-transform"
                >
                  {t('nav.bookNow')} <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <a href="#features" className="inline-flex items-center justify-center rounded-full px-6 py-4 border-2 border-[#004E64]/20 hover:border-[#004E64] bg-transparent text-[#004E64] font-medium transition-colors">
                  {t('hero.learnMore')}
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-[#004E64]/10">
                <div>
                  <p className="font-serif text-3xl font-bold text-[#004E64]">€{config?.price_per_bag_per_day || 5}</p>
                  <p className="text-sm text-[#5A6B7C]">{t('hero.perBagDay')}</p>
                </div>
                <div>
                  <p className="font-serif text-3xl font-bold text-[#004E64]">€{config?.price_per_bag_per_hour || 1.5}</p>
                  <p className="text-sm text-[#5A6B7C]">{t('hero.perBagHour')}</p>
                </div>
                <div>
                  <p className="font-serif text-3xl font-bold text-[#004E64]">24/7</p>
                  <p className="text-sm text-[#5A6B7C]">{t('hero.support')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative min-h-[400px] lg:min-h-0">
            <img src={heroImage} alt="Bari" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#004E64]/30 to-transparent lg:from-[#F5F5F0] lg:via-transparent lg:to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('features.tagline')}</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight text-[#004E64]">{t('features.title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.length > 0 ? features.map((feature, index) => (
              <div key={index} className="feature-card bg-[#F5F5F0] rounded-2xl p-8 border border-transparent hover:border-[#004E64]/10">
                <div className={`w-14 h-14 ${index === 1 ? 'bg-[#FFB400]' : 'bg-[#004E64]'} rounded-2xl flex items-center justify-center mb-6`}>
                  {index === 0 && <ShieldCheck className={`w-7 h-7 ${index === 1 ? 'text-[#004E64]' : 'text-white'}`} />}
                  {index === 1 && <Clock className="w-7 h-7 text-[#004E64]" />}
                  {index === 2 && <MapPin className="w-7 h-7 text-white" />}
                </div>
                <h3 className="font-sans text-xl font-bold text-[#004E64] mb-3">{getContent(feature.title, language)}</h3>
                <p className="text-[#5A6B7C]">{getContent(feature.description, language)}</p>
              </div>
            )) : (
              <>
                <div className="feature-card bg-[#F5F5F0] rounded-2xl p-8 border border-transparent hover:border-[#004E64]/10">
                  <div className="w-14 h-14 bg-[#004E64] rounded-2xl flex items-center justify-center mb-6">
                    <ShieldCheck className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-sans text-xl font-bold text-[#004E64] mb-3">{t('features.secure.title')}</h3>
                  <p className="text-[#5A6B7C]">{t('features.secure.description')}</p>
                </div>
                <div className="feature-card bg-[#F5F5F0] rounded-2xl p-8 border border-transparent hover:border-[#004E64]/10">
                  <div className="w-14 h-14 bg-[#FFB400] rounded-2xl flex items-center justify-center mb-6">
                    <Clock className="w-7 h-7 text-[#004E64]" />
                  </div>
                  <h3 className="font-sans text-xl font-bold text-[#004E64] mb-3">{t('features.flexible.title')}</h3>
                  <p className="text-[#5A6B7C]">{t('features.flexible.description')}</p>
                </div>
                <div className="feature-card bg-[#F5F5F0] rounded-2xl p-8 border border-transparent hover:border-[#004E64]/10">
                  <div className="w-14 h-14 bg-[#004E64] rounded-2xl flex items-center justify-center mb-6">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-sans text-xl font-bold text-[#004E64] mb-3">{t('features.central.title')}</h3>
                  <p className="text-[#5A6B7C]">{t('features.central.description')}</p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            <div className="rounded-2xl overflow-hidden h-64 md:h-80">
              <img src={featureImage1} alt="Luggage storage" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden h-64 md:h-80">
              <img src={featureImage2} alt="Happy travelers" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking-form" className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('booking.tagline')}</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight text-[#004E64] mb-4">{t('booking.title')}</h2>
            <p className="text-[#5A6B7C]">{t('booking.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="card-booking form-card">
            <div className="space-y-6 mb-8">
              <h3 className="font-sans text-lg font-bold text-[#004E64] flex items-center gap-2">
                <Mail className="w-5 h-5" /> {t('booking.contactInfo')}
              </h3>

              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.fullName')} *</Label>
                <Input type="text" required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg" placeholder={t('booking.fullNamePlaceholder')} />
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.email')}</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg" placeholder={t('booking.emailPlaceholder')} />
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.mobile')} *</Label>
                <PhoneInput
                  value={formData.mobile_number}
                  onChange={(value) => setFormData({ ...formData, mobile_number: value })}
                  placeholder="333 123 4567"
                  required
                  testId="mobile"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.whatsapp')}</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={sameAsWhatsApp}
                      onCheckedChange={(checked) => {
                        setSameAsWhatsApp(checked);
                        if (checked) {
                          setFormData(prev => ({ ...prev, whatsapp_number: prev.mobile_number }));
                        }
                      }}
                      data-testid="same-as-mobile"
                    />
                    <span className="text-sm text-[#5A6B7C]">{t('booking.sameAsMobile') || 'Same as mobile'}</span>
                  </label>
                </div>
                <PhoneInput
                  value={sameAsWhatsApp ? formData.mobile_number : formData.whatsapp_number}
                  onChange={(value) => {
                    if (!sameAsWhatsApp) {
                      setFormData({ ...formData, whatsapp_number: value });
                    }
                  }}
                  placeholder="333 123 4567"
                  testId="whatsapp"
                />
              </div>
            </div>

            <div className="space-y-6 mb-8 pt-8 border-t border-[#004E64]/10">
              <h3 className="font-sans text-lg font-bold text-[#004E64] flex items-center gap-2">
                <Luggage className="w-5 h-5" /> {t('booking.storageDetails')}
              </h3>

              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.numberOfBags')} *</Label>
                <Select value={formData.number_of_bags.toString()} onValueChange={(value) => setFormData({ ...formData, number_of_bags: parseInt(value) })}>
                  <SelectTrigger className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg">
                    <SelectValue placeholder={t('booking.selectBags')} />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? t('booking.bag') : t('booking.bags')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.pricingType') || 'Pricing Type'} *</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pricing_type: "auto" })}
                    className={`p-4 rounded-xl border-2 transition-all ${formData.pricing_type === "auto"
                      ? "border-[#004E64] bg-[#004E64]/5"
                      : "border-transparent bg-[#F9F9F7] hover:border-[#004E64]/20"
                      }`}
                    data-testid="pricing-auto"
                  >
                    <p className="font-bold text-[#004E64]">{t('booking.pricingAuto') || 'Auto'}</p>
                    <p className="text-xs text-[#5A6B7C] mt-1">{t('booking.pricingAutoDesc') || 'Best rate'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pricing_type: "hourly" })}
                    className={`p-4 rounded-xl border-2 transition-all ${formData.pricing_type === "hourly"
                      ? "border-[#FFB400] bg-[#FFB400]/10"
                      : "border-transparent bg-[#F9F9F7] hover:border-[#FFB400]/20"
                      }`}
                    data-testid="pricing-hourly"
                  >
                    <p className="font-bold text-[#004E64]">€{config?.price_per_bag_per_hour || 1.5}</p>
                    <p className="text-xs text-[#5A6B7C] mt-1">{t('booking.perHour') || 'per hour'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pricing_type: "daily" })}
                    className={`p-4 rounded-xl border-2 transition-all ${formData.pricing_type === "daily"
                      ? "border-[#FFB400] bg-[#FFB400]/10"
                      : "border-transparent bg-[#F9F9F7] hover:border-[#FFB400]/20"
                      }`}
                    data-testid="pricing-daily"
                  >
                    <p className="font-bold text-[#004E64]">€{config?.price_per_bag_per_day || 5}</p>
                    <p className="text-xs text-[#5A6B7C] mt-1">{t('booking.perDay') || 'per day'}</p>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.dropoffDate')} *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-14 bg-[#F9F9F7] border-transparent hover:bg-[#F9F9F7] justify-start text-left font-normal text-lg rounded-xl">
                        <CalendarIcon className="mr-2 h-5 w-5 text-[#004E64]" />
                        {formData.dropoff_date ? format(formData.dropoff_date, "PPP") : <span className="text-[#5A6B7C]">{t('booking.selectDate')}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={formData.dropoff_date} onSelect={(date) => setFormData({ ...formData, dropoff_date: date })} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.dropoffTime')} *</Label>
                  <Select value={formData.dropoff_time} onValueChange={(value) => setFormData({ ...formData, dropoff_time: value })}>
                    <SelectTrigger className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg">
                      <SelectValue placeholder={t('booking.selectTime')} />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.pickupDate')} *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-14 bg-[#F9F9F7] border-transparent hover:bg-[#F9F9F7] justify-start text-left font-normal text-lg rounded-xl">
                        <CalendarIcon className="mr-2 h-5 w-5 text-[#004E64]" />
                        {formData.pickup_date ? format(formData.pickup_date, "PPP") : <span className="text-[#5A6B7C]">{t('booking.selectDate')}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={formData.pickup_date} onSelect={(date) => setFormData({ ...formData, pickup_date: date })} disabled={(date) => date < (formData.dropoff_date || new Date(new Date().setHours(0, 0, 0, 0)))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="mb-2 block text-sm font-bold tracking-wide text-[#004E64]/70 uppercase">{t('booking.pickupTime')} *</Label>
                  <Select value={formData.pickup_time} onValueChange={(value) => setFormData({ ...formData, pickup_time: value })}>
                    <SelectTrigger className="h-14 bg-[#F9F9F7] border-transparent focus:border-[#004E64] rounded-xl text-lg">
                      <SelectValue placeholder={t('booking.selectTime')} />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="mb-8 pt-8 border-t border-[#004E64]/10">
              <div className="bg-[#F5F5F0] rounded-xl p-4 flex items-center gap-3">
                <Euro className="w-6 h-6 text-[#004E64]" />
                <div>
                  <p className="font-bold text-[#004E64]">{t('booking.payAtLocation')}</p>
                  <p className="text-sm text-[#5A6B7C]">{t('booking.payAtLocationDesc')}</p>
                </div>
              </div>
            </div>

            {priceInfo && (
              <div className="bg-[#004E64] rounded-2xl p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-sm uppercase tracking-wide">{t('booking.totalPrice')}</p>
                    <p className="text-white text-sm mt-1">{priceInfo.breakdown}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-4xl font-bold text-[#FFB400]">€{priceInfo.total_price}</p>
                    <p className="text-white/70 text-sm">{priceInfo.duration}</p>
                  </div>
                </div>
              </div>
            )}

            {isCalculating && (
              <div className="flex items-center justify-center py-4 mb-8">
                <div className="spinner mr-3"></div>
                <span className="text-[#5A6B7C]">{t('booking.calculating')}</span>
              </div>
            )}

            <Button type="submit" disabled={isSubmitting || !priceInfo} className="w-full h-16 bg-[#FFB400] hover:bg-[#E5A200] text-[#004E64] font-bold text-lg rounded-full shadow-lg hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0">
              {isSubmitting ? (
                <><div className="spinner mr-3 border-[#004E64]/20 border-t-[#004E64]"></div>{t('booking.processing')}</>
              ) : (
                <>{t('booking.confirmBooking')} <ChevronRight className="ml-2 w-5 h-5" /></>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-20 md:py-32 px-6 md:px-12 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('location.tagline')}</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight text-[#004E64] mb-4">{t('location.title')}</h2>
            <p className="text-[#5A6B7C] flex items-center justify-center gap-2">
              <MapPin className="w-5 h-5 text-[#FFB400]" />
              {config?.business_address || "Via Andrea da Bari, 122, Bari, Italy"}
            </p>
          </div>

          <div className="map-container h-[500px] bg-white">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3004.8456193726957!2d16.8663!3d41.1256!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDA3JzMyLjIiTiAxNsKwNTEnNTguNyJF!5e0!3m2!1sen!2sit!4v1234567890"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" title="Location"
            ></iframe>
          </div>

          <div className="mt-8 text-center">
            <a href={config?.google_maps_link || "https://www.google.com/maps/search/?api=1&query=Via+Andrea+da+Bari+122+Bari+Italy"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#004E64] hover:bg-[#003848] text-white rounded-full px-8 py-4 font-medium shadow-lg hover:-translate-y-1 transition-all">
              <MapPin className="w-5 h-5" />
              {t('location.openMaps')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#004E64] text-white py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-14 w-auto object-contain" />
                ) : (
                  <Luggage className="w-8 h-8 text-[#FFB400]" />
                )}
                <span className="font-serif text-xl font-semibold">{config?.business_name}</span>
              </div>
              <p className="text-white/70">{footerDesc}</p>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-[#FFB400]">{t('footer.contact')}</h4>
              <div className="space-y-3">
                <a href={`mailto:${config?.business_email || 'info@baribird.com'}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" /> {config?.business_email || 'estiakhossain90@gmail.com'}
                </a>
                <a href={`https://wa.me/${(config?.admin_whatsapp || '+393205608143').replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                  <MessageCircle className="w-4 h-4" /> {config?.admin_whatsapp || '+39 320 560 8143'}
                </a>
                <p className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-4 h-4" /> {config?.business_address || "Via Andrea da Bari, 122, Bari"}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-[#FFB400]">{t('footer.pricing')}</h4>
              <div className="space-y-2 text-white/70">
                <p>€{config?.price_per_bag_per_day || 5} {t('footer.perDay')}</p>
                <p>€{config?.price_per_bag_per_hour || 1.5} {t('footer.perHour')}</p>
                <p className="text-sm">{t('footer.hourlyNote')}</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/50 text-sm">
            © {new Date().getFullYear()} {config?.business_name || 'Bari Bird Luggage Storage'}. {t('footer.rights')}
          </div>
        </div>
      </footer>
    </div>
  );
}
